import env from '@trashlab/viteapp/src/env';
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  GoogleMap,
  Circle,
  useJsApiLoader,
  type Libraries,
} from '@react-google-maps/api';
import { debounce } from 'lodash-es';
import { useTrashlabOrganization } from '@trashlab/viteapp/src/hooks/shared/use-trashlab-organization';
import { formatRadiusRangePreview } from '@trashlab/viteapp/src/components/pricing/radius-range-utils';

interface MapConfiguratorProps {
  latitude: number;
  longitude: number;
  minRadius?: number;
  radius: number;
  onLocationChange: (location: {
    latitude: number;
    longitude: number;
    radius: number;
  }) => void;
  useDefaultAddress?: boolean; // prop to activate default address functionality (when create -> true, when edit -> false)
  /** Height of the map container. Can be a number (px) or CSS string. Defaults to 400 */
  height?: number | string;
}

export const initialRadiusBasedCenter = {
  lat: 37.30952,
  lng: -121.919068,
};

const milesToMeters = (value: number) => value * 1609.34;
const GOOGLE_MAPS_LIBRARIES: Libraries = ['places', 'geometry', 'marker'];

const RadiusBasedPricingMapConfigurator = ({
  latitude,
  longitude,
  minRadius = 0,
  radius,
  onLocationChange,
  useDefaultAddress = true,
  height = 400,
}: MapConfiguratorProps) => {
  const organization = useTrashlabOrganization();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const isUserAdjustingRadiusRef = useRef(false);

  const [circleCenter, setCircleCenter] = useState({
    lat: latitude ?? initialRadiusBasedCenter.lat,
    lng: longitude ?? initialRadiusBasedCenter.lng,
  });
  const [circleRadius, setCircleRadius] = useState(
    milesToMeters(radius ?? 50),
  );
  const [isDefaultAddressSet, setIsDefaultAddressSet] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ?? '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const updateFormValues = useCallback(
    debounce((center: google.maps.LatLng, radius: number) => {
      onLocationChange({
        latitude: center.lat(),
        longitude: center.lng(),
        radius: Number((radius / 1609.34).toFixed(2)),
      });
    }, 300),
    [onLocationChange],
  );

  const onCircleDragEnd = useCallback(() => {
    if (circleRef.current) {
      const center = circleRef.current.getCenter();
      if (center) {
        const newCenter = { lat: center.lat(), lng: center.lng() };
        setCircleCenter(newCenter);
        updateFormValues(center, circleRadius);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
  }, [circleRadius, updateFormValues]);

  const onRadiusChange = useCallback(() => {
    if (circleRef.current) {
      const newRadius = circleRef.current.getRadius();
      setCircleRadius(newRadius);
      if (!isUserAdjustingRadiusRef.current) {
        return;
      }
      const center = circleRef.current.getCenter();
      if (center) {
        updateFormValues(center, newRadius);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    }
  }, [updateFormValues]);

  const onMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const newCenter = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setCircleCenter(newCenter);
        updateFormValues(e.latLng, circleRadius);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    },
    [circleRadius, updateFormValues],
  );

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry && place.geometry.location) {
        const newCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        setCircleCenter(newCenter);
        updateFormValues(place.geometry.location, circleRadius);
        if (map) {
          map.panTo(newCenter);
        }
      } else {
        console.error('Place geometry or location is undefined', place);
      }
    }
  }, [circleRadius, updateFormValues, map]);

  useEffect(() => {
    const nextCenter = {
      lat: latitude ?? initialRadiusBasedCenter.lat,
      lng: longitude ?? initialRadiusBasedCenter.lng,
    };

    setCircleCenter((current) =>
      current.lat === nextCenter.lat && current.lng === nextCenter.lng
        ? current
        : nextCenter,
    );
  }, [latitude, longitude]);

  useEffect(() => {
    const nextRadius = milesToMeters(radius ?? 50);

    setCircleRadius((current) =>
      current === nextRadius ? current : nextRadius,
    );
  }, [radius]);

  useEffect(() => {
    if (isLoaded && inputRef.current) {
      autocompleteRef.current = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['geocode'],
        },
      );
      autocompleteRef.current.addListener('place_changed', onPlaceChanged);
    }
  }, [isLoaded, onPlaceChanged]);

  useEffect(() => {
    if (
      useDefaultAddress &&
      organization.addressString &&
      map &&
      !isDefaultAddressSet
    ) {
      if (inputRef.current) {
        inputRef.current.value = organization.addressString;
        if (autocompleteRef.current) {
          const service = new google.maps.places.PlacesService(
            map,
          );
          service.textSearch(
            { query: organization.addressString },
            (results, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                results &&
                results[0]
              ) {
                const place = results[0];
                if (place.geometry && place.geometry.location) {
                  const newCenter = {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng(),
                  };
                  setCircleCenter(newCenter);
                  updateFormValues(place.geometry.location, circleRadius);
                  if (map) {
                    map.panTo(newCenter);
                  }
                  setIsDefaultAddressSet(true);
                }
              }
            },
          );
        }
      } else {
        const checkInputReady = setInterval(() => {
          if (inputRef.current && organization.addressString) {
            inputRef.current.value = organization.addressString;
            clearInterval(checkInputReady);
            setIsDefaultAddressSet(true);
          }
        }, 100);
      }
    }
  }, [
    useDefaultAddress,
    organization.addressString,
    map,
    circleRadius,
    updateFormValues,
    isDefaultAddressSet,
  ]);

  // Compute map container style with dynamic height
  const mapContainerStyle = {
    width: '100%',
    height: typeof height === 'number' ? `${height}px` : height,
    minHeight: '300px',
  };
  const previewLabel = formatRadiusRangePreview(minRadius, circleRadius / 1609.34);

  return isLoaded ? (
    <div className="flex flex-col flex-1" style={{ minHeight: mapContainerStyle.minHeight }}>
      <div className="mb-2 flex items-center justify-between text-sm text-gray-600">
        <span>Map preview</span>
        <span className="font-medium text-gray-900">{previewLabel}</span>
      </div>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={circleCenter}
        zoom={12}
        onLoad={(mapInstance) => setMap(mapInstance)}
        onClick={onMapClick}
      >
        <Circle
          center={circleCenter}
          radius={circleRadius}
          options={{
            fillColor: 'red',
            fillOpacity: 0.2,
            strokeColor: 'red',
            strokeOpacity: 1,
            strokeWeight: 2,
            editable: true,
            draggable: true,
          }}
          onLoad={(circleInstance) => {
            circleRef.current = circleInstance;
          }}
          onMouseDown={() => {
            isUserAdjustingRadiusRef.current = true;
          }}
          onMouseUp={() => {
            isUserAdjustingRadiusRef.current = false;
          }}
          onRadiusChanged={onRadiusChange}
          onDragEnd={() => {
            isUserAdjustingRadiusRef.current = false;
            onCircleDragEnd();
          }}
        />
        {minRadius > 0 && (
          <Circle
            center={circleCenter}
            radius={milesToMeters(minRadius)}
            options={{
              fillColor: '#ffffff',
              fillOpacity: 0.22,
              strokeColor: 'red',
              strokeOpacity: 0.7,
              strokeWeight: 1,
              clickable: false,
              draggable: false,
              editable: false,
            }}
          />
        )}
      </GoogleMap>
      <input
        ref={inputRef}
        type="text"
        placeholder="Enter an address to quickly move the zone"
        style={{
          width: '100%',
          marginTop: '10px',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
        }}
      />
    </div>
  ) : (
    <div>Loading...</div>
  );
};

export default RadiusBasedPricingMapConfigurator;
